import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import type {
  GetStoreListQuery,
  GetSellerPasswordMutation,
  GetSellerPasswordMutationVariables,
  UpdateStoreSignupStateToApprovedMutation,
  UpdateStoreSignupStateToApprovedMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

export const GET_STORE_LIST = gql(`
  query GetStoreList {
    getStoreList {
      ok
      error
      result {
        id
        signupState
        title
        img
        address
        createdAt
        isDeleted
        isHidden
        seller {
          id
          name
          contactNumber
          signupSource
        }
        pack {
          category
          price
          quantity
          description
        }
      }
    }
  }
`);

const GET_SELLER_PASSWORD = gql(`
  mutation GetSellerPassword($storeId: Int!) {
    getSellerPassword(storeId: $storeId) {
      ok
      error
      password
    }
  }
`);

const UPDATE_STORE_SIGNUP_STATE_TO_APPROVED = gql(`
  mutation UpdateStoreSignupStateToApproved($storeId: Int!, $couponAmount: Int) {
    updateStoreSignupStateToApproved(storeId: $storeId, couponAmount: $couponAmount) {
      ok
      error
    }
  }
`);

const PRESET_AMOUNTS = [1000, 2000, 3000, 5000];

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

interface ApprovalModalState {
  storeId: number;
  storeName: string;
}

const AdminStoreSignupRequest = () => {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [approvalModal, setApprovalModal] = useState<ApprovalModalState | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(3000);
  const [customAmount, setCustomAmount] = useState("");
  const [noCoupon, setNoCoupon] = useState(false);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [updateStoreSignupStateToApproved, { loading: updating }] = useMutation<
    UpdateStoreSignupStateToApprovedMutation,
    UpdateStoreSignupStateToApprovedMutationVariables
  >(UPDATE_STORE_SIGNUP_STATE_TO_APPROVED, {
    onCompleted: (res) => {
      if (res.updateStoreSignupStateToApproved.ok) {
        notify("승인 완료 메시지를 성공적으로 전송했습니다.");
        setApprovalModal(null);
        refetch();
      } else {
        notifyError(
          res.updateStoreSignupStateToApproved.error ??
            "승인 처리에 실패했습니다.",
        );
      }
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });
  const [getSellerPassword] = useMutation<
    GetSellerPasswordMutation,
    GetSellerPasswordMutationVariables
  >(GET_SELLER_PASSWORD);

  const handleCopyPassword = async (storeId: number) => {
    try {
      const { data: res } = await getSellerPassword({
        variables: { storeId },
        fetchPolicy: "no-cache",
      });

      if (res?.getSellerPassword.ok && res?.getSellerPassword.password) {
        await navigator.clipboard.writeText(res.getSellerPassword.password);
        notify("비밀번호가 클립보드에 복사되었습니다.");
      } else {
        notifyError(
          res?.getSellerPassword.error ?? "비밀번호를 가져오는데 실패했습니다.",
        );
      }
    } catch (e) {
      notifyError(
        e instanceof Error ? e.message : "비밀번호 복사에 실패했습니다.",
      );
    }
  };

  const openApprovalModal = (store: StoreRow) => {
    setSelectedPreset(3000);
    setCustomAmount("");
    setNoCoupon(false);
    setApprovalModal({ storeId: store.id, storeName: store.title });
  };

  const resolvedCouponAmount = (): number | null => {
    if (noCoupon) return null;
    if (selectedPreset != null) return selectedPreset;
    const parsed = parseInt(customAmount.replace(/,/g, ""), 10);
    return isNaN(parsed) || parsed <= 0 ? null : parsed;
  };

  const handleConfirmApproval = () => {
    if (!approvalModal) return;
    const couponAmount = resolvedCouponAmount();
    updateStoreSignupStateToApproved({
      variables: {
        storeId: approvalModal.storeId,
        couponAmount: couponAmount ?? undefined,
      },
    });
  };

  const fullList = data?.getStoreList?.ok
    ? (data.getStoreList.result ?? [])
    : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter((store) => store.title.toLowerCase().includes(keyword))
    : fullList;

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminListDetailLayout
        title="매장 입점신청"
        description="매장을 선택하여 상세 정보를 확인하고, 승인·비밀번호 복사 등의 작업을 수행하세요."
        leftRatio={7}
        rightRatio={3}
        leftPanel={{
          title: "매장 목록",
          searchPlaceholder: "매장명으로 검색",
          searchValue: searchKeyword,
          onSearchChange: setSearchKeyword,
          children: (
            <AdminStoreTableWithMobileCards
              stores={list.map((s) => ({
                id: s.id,
                title: s.title,
                img: s.img,
                address: s.address ?? undefined,
              }))}
              selectionMode="single"
              selectedIds={selectedStore ? [selectedStore.id] : []}
              onSelect={(store) =>
                setSelectedStore(list.find((x) => x.id === store.id) ?? null)
              }
              extraHeaders={
                <>
                  <th className="px-4 py-2 font-medium text-gray-700">
                    가입상태
                  </th>
                  <th className="px-4 py-2 font-medium text-gray-700">
                    가입일
                  </th>
                </>
              }
              renderExtraCells={(store) => {
                const full = list.find((x) => x.id === store.id);
                if (!full) return null;
                return (
                  <>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                          {
                            "bg-green-100 text-green-800":
                              full.signupState === "승인",
                            "bg-yellow-100 text-yellow-800":
                              full.signupState === "요청",
                            "bg-red-100 text-red-800":
                              full.signupState === "거절",
                            "bg-orange-100 text-orange-800":
                              full.signupState === "요청완료",
                          }
                        )}
                      >
                        {full.signupState}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {new Date(full.createdAt).toLocaleDateString()}
                    </td>
                  </>
                );
              }}
              renderExtraCard={(store) => {
                const full = list.find((x) => x.id === store.id);
                if (!full) return null;
                return (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span
                      className={clsx(
                        "px-2 py-0.5 inline-flex font-semibold rounded-full text-xs",
                        {
                          "bg-green-100 text-green-800":
                            full.signupState === "승인",
                          "bg-yellow-100 text-yellow-800":
                            full.signupState === "요청",
                          "bg-red-100 text-red-800":
                            full.signupState === "거절",
                          "bg-orange-100 text-orange-800":
                            full.signupState === "요청완료",
                        }
                      )}
                    >
                      {full.signupState}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(full.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                );
              }}
              emptyText={
                keyword ? "검색 결과가 없습니다." : "등록된 매장이 없습니다."
              }
            />
          ),
        }}
        rightPanel={{
          title: "매장 상세 정보",
          children: selectedStore ? (
            <>
              <div className="text-sm text-gray-700">
                <span className="font-medium">선택 매장:</span>{" "}
                {selectedStore.title} (ID: {selectedStore.id})
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  {selectedStore.img ? (
                    <ResponsiveCDNImage
                      src={selectedStore.img}
                      alt={selectedStore.title}
                      className="w-16 h-16 rounded object-cover"
                      sizeRatio={0.15}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded" />
                  )}
                  <div>
                    <div className="font-medium text-gray-800">
                      {selectedStore.title}
                    </div>
                    <div className="text-gray-600">
                      {selectedStore.address ?? "—"}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700">
                  <div>
                    <span className="font-medium text-gray-800">가입상태:</span>{" "}
                    {selectedStore.signupState}
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">가입일:</span>{" "}
                    {new Date(selectedStore.createdAt).toLocaleDateString()}
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">연락처:</span>{" "}
                    {selectedStore.seller?.contactNumber ?? "—"}
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">가입경로:</span>{" "}
                    {selectedStore.seller?.signupSource ?? "—"}
                  </div>
                </div>

                {selectedStore.pack && (
                  <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700">
                    <div className="font-medium text-gray-800 mb-2">
                      Pack 정보
                    </div>
                    <div>카테고리: {selectedStore.pack.category || "—"}</div>
                    <div>
                      가격:{" "}
                      {selectedStore.pack.price != null
                        ? selectedStore.pack.price.toLocaleString() + "원"
                        : "—"}
                    </div>
                    <div>
                      수량:{" "}
                      {selectedStore.pack.quantity != null
                        ? selectedStore.pack.quantity + "개"
                        : "—"}
                    </div>
                    {selectedStore.pack.description && (
                      <div className="pt-2 border-t border-gray-200">
                        <span className="font-medium text-gray-800">설명:</span>
                        <div className="mt-1 text-gray-600 whitespace-pre-wrap">
                          {selectedStore.pack.description}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => handleCopyPassword(selectedStore.id)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200"
                >
                  비밀번호 복사
                </button>
                {selectedStore.signupState === "요청완료" && (
                  <button
                    type="button"
                    disabled={updating}
                    onClick={() => openApprovalModal(selectedStore)}
                    className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? "처리 중..." : "승인"}
                  </button>
                )}
              </div>
            </>
          ) : null,
        }}
      />

      {/* 승인 + 쿠폰 발행 모달 */}
      {approvalModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !updating && setApprovalModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                매장 승인
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                <span className="font-medium text-gray-800">
                  {approvalModal.storeName}
                </span>{" "}
                매장을 승인하고 알림톡을 발송합니다.
              </p>
            </div>

            {/* 쿠폰 발행 섹션 */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  쿠폰 발행
                </span>
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={noCoupon}
                    onChange={(e) => {
                      setNoCoupon(e.target.checked);
                      if (e.target.checked) {
                        setSelectedPreset(null);
                        setCustomAmount("");
                      }
                    }}
                    className="rounded"
                  />
                  쿠폰 발행 안함
                </label>
              </div>

              {!noCoupon && (
                <>
                  {/* 프리셋 버튼 */}
                  <div className="flex gap-2 flex-wrap">
                    {PRESET_AMOUNTS.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => {
                          setSelectedPreset(amount);
                          setCustomAmount("");
                        }}
                        className={clsx(
                          "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                          selectedPreset === amount
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                        )}
                      >
                        {amount.toLocaleString()}원
                      </button>
                    ))}
                  </div>

                  {/* 직접 입력 */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="직접 입력 (예: 4000)"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setSelectedPreset(null);
                      }}
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-500">원</span>
                  </div>

                  <p className="text-xs text-gray-400">
                    알림받기(LIKE) 필수 조건 · 1장 · 승인일로부터 30일
                  </p>
                </>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                disabled={updating}
                onClick={() => setApprovalModal(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                disabled={updating}
                onClick={handleConfirmApproval}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? "처리 중..." : "승인"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminStoreSignupRequest;
